
import type { FriendAskRow, FriendList, FriendRow, Guild, GuildMember } from "../net/messages";
import { TEXTURE_ART, elem, installChrome, styleInstaller } from "./chrome";
import { yieldToCandidateWindow } from "./ime";

export interface FriendActions {
  /** Close the shared social surface when the visible messenger is dismissed. */
  onClose?: () => void;
  /** Ask to be on somebody's list. Nothing appears until they answer. */
  onAdd?: (name: string) => void;
  /** Accept or decline, naming the token of the row the button belongs to. */
  onAnswer?: (token: number, accept: boolean) => void;
  /** Drop somebody from this player's own list. One direction. */
  onRemove?: (name: string) => void;
  onBlock?: (
    whisper: boolean,
    invite: boolean,
    exchange: boolean,
    partyInvite: boolean,
    guildInvite: boolean,
  ) => void;
  /** Start a whisper to this name. The chat box owns the text. */
  onWhisper?: (name: string) => void;
  /** Open the fuller Guild window. This panel draws its own inline Guild
   *  group too (see the module docstring) -- this action is the original's
   *  separate `GuildButton` press (`uimessenger.py:498-499`), a peer
   *  affordance to that group, not a substitute for it. */
  onOpenGuild?: () => void;
}

/** `messengerwindow.py` window width. */
export const MESSENGER_WIDTH = 170;
/** `messengerwindow.py` window height. */
export const MESSENGER_HEIGHT = 300;
/** `MESSENGER_TITLE` (`locale_interface.txt:198`). */
export const MESSENGER_TITLE = "Friends";

const STYLE_ID = "friends-style";

const ICON = {
  add: `${TEXTURE_ART}/game/windows/messenger_add_friend_01.png`,
  addOver: `${TEXTURE_ART}/game/windows/messenger_add_friend_02.png`,
  addDown: `${TEXTURE_ART}/game/windows/messenger_add_friend_03.png`,
  addOff: `${TEXTURE_ART}/game/windows/messenger_add_friend_04.png`,
  whisper: `${TEXTURE_ART}/game/windows/messenger_whisper_01.png`,
  whisperOver: `${TEXTURE_ART}/game/windows/messenger_whisper_02.png`,
  whisperDown: `${TEXTURE_ART}/game/windows/messenger_whisper_03.png`,
  whisperOff: `${TEXTURE_ART}/game/windows/messenger_whisper_04.png`,
  remove: `${TEXTURE_ART}/game/windows/messenger_delete_01.png`,
  removeOver: `${TEXTURE_ART}/game/windows/messenger_delete_02.png`,
  removeDown: `${TEXTURE_ART}/game/windows/messenger_delete_03.png`,
  removeOff: `${TEXTURE_ART}/game/windows/messenger_delete_04.png`,
  guild: `${TEXTURE_ART}/game/windows/messenger_guild_01.png`,
  guildOver: `${TEXTURE_ART}/game/windows/messenger_guild_02.png`,
  guildDown: `${TEXTURE_ART}/game/windows/messenger_guild_03.png`,
  guildOff: `${TEXTURE_ART}/game/windows/messenger_guild_04.png`,
} as const;

const STYLE = `
.friends {
  position: fixed;
  /* messengerwindow.py: x = SCREEN_WIDTH - 200, y = SCREEN_HEIGHT - 400 - 50 */
  left: calc(100% - 200px);
  top: calc(100% - 450px);
  z-index: 58;
  width: ${MESSENGER_WIDTH}px;
  height: ${MESSENGER_HEIGHT}px;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  pointer-events: auto;
  overflow: hidden;
}
.friends[hidden] { display: none; }
.friends .m2-titlebar {
  width: 155px;
  left: 8px;
  top: 7px;
  cursor: move;
  user-select: none;
}
.friends-body {
  position: absolute;
  left: 10px;
  top: 32px;
  right: 10px;
  bottom: 78px;
  overflow-x: hidden;
  overflow-y: auto;
}
.friends-group-header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 1px 2px;
  margin-bottom: 1px;
  cursor: pointer;
  color: #d8c7a6;
  font: 11px/16px Tahoma, "MS Sans Serif", sans-serif;
}
.friends-group-header:hover { background: rgba(0, 0, 112, 0.35); }
.friends-group-toggle { width: 12px; flex: 0 0 auto; text-align: center; }
.friends-guild-header { margin-top: 4px; }
.friends-list { display: flex; flex-direction: column; gap: 0; }
.friends-row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 1px 2px 1px 14px;
  cursor: pointer;
  font: 12px/16px Tahoma, "MS Sans Serif", sans-serif;
  color: #e8dcc0;
}
.friends-row:hover { background: rgba(0, 0, 112, 0.35); }
.friends-row-selected { background: rgba(0, 0, 178, 0.7); }
.friends-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #5a5040;
  flex: 0 0 auto;
}
.friends-row-online .friends-dot { background: #7fd68a; }
/* The Family group's one row. Its own class rather than .friends-row: it is
   never selectable and never whispered to, and every query meaning "a friend
   or a guild member" must not find the spouse. */
.friends-family-row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 1px 2px 1px 14px;
  font: 12px/16px Tahoma, "MS Sans Serif", sans-serif;
  color: #e8dcc0;
}
.friends-family-row[hidden] { display: none; }
.friends-family-row-online .friends-dot { background: #7fd68a; }
.friends-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.friends-row-offline .friends-name { opacity: 0.55; }
.friends-empty {
  color: #a89878;
  font: 11px/16px Tahoma, "MS Sans Serif", sans-serif;
  padding: 1px 2px 1px 14px;
}
.friends-add {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 52px;
  display: flex;
  gap: 4px;
  align-items: center;
}
.friends-add[hidden] { display: none; }
.friends-add-name {
  flex: 1;
  min-width: 0;
  height: 18px;
  padding: 0 4px;
  border: 1px solid #6a5a3a;
  background: rgba(0, 0, 0, 0.45);
  color: #ead7ae;
  font: 11px/18px Tahoma, "MS Sans Serif", sans-serif;
}
.friends-add-name:focus { outline: none; border-color: #8d7044; }
.friends-ask-submit {
  flex: 0 0 auto;
  height: 18px;
  padding: 0 6px;
  border: 1px solid #8d7044;
  background: #2a2418;
  color: #ead7ae;
  font: 11px/16px Tahoma, "MS Sans Serif", sans-serif;
  cursor: pointer;
}
.friends-ask-submit:hover { background: #3a3220; }
.friends-switches {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 34px;
  display: flex;
  flex-direction: column;
  gap: 0;
  color: #a89878;
  font: 10px/12px Tahoma, "MS Sans Serif", sans-serif;
}
.friends-switch { display: flex; align-items: center; gap: 4px; cursor: pointer; }
.friends-icons {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 10px;
  height: 22px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 19px;
}
.friends-icon {
  width: 21px;
  height: 18px;
  padding: 0;
  border: 0;
  background: center / contain no-repeat;
  image-rendering: pixelated;
  cursor: pointer;
  color: transparent;
  font: 0/0 a;
}
.friends-icon:disabled { cursor: default; }
.friends-add-button { background-image: url("${ICON.add}"); }
.friends-add-button:hover:not(:disabled) { background-image: url("${ICON.addOver}"); }
.friends-add-button:active:not(:disabled) { background-image: url("${ICON.addDown}"); }
.friends-add-button:disabled { background-image: url("${ICON.addOff}"); }
.friends-whisper { background-image: url("${ICON.whisper}"); }
.friends-whisper:hover:not(:disabled) { background-image: url("${ICON.whisperOver}"); }
.friends-whisper:active:not(:disabled) { background-image: url("${ICON.whisperDown}"); }
.friends-whisper:disabled { background-image: url("${ICON.whisperOff}"); }
.friends-remove { background-image: url("${ICON.remove}"); }
.friends-remove:hover:not(:disabled) { background-image: url("${ICON.removeOver}"); }
.friends-remove:active:not(:disabled) { background-image: url("${ICON.removeDown}"); }
.friends-remove:disabled { background-image: url("${ICON.removeOff}"); }
.friends-guild-button { background-image: url("${ICON.guild}"); }
.friends-guild-button:hover:not(:disabled) { background-image: url("${ICON.guildOver}"); }
.friends-guild-button:active:not(:disabled) { background-image: url("${ICON.guildDown}"); }
.friends-guild-button:disabled { background-image: url("${ICON.guildOff}"); }

.friends-ask {
  position: fixed;
  left: 50%;
  top: 30%;
  transform: translateX(-50%);
  z-index: 63;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: auto;
}
.friends-ask[hidden] { display: none; }
.friends-ask-card {
  width: 220px;
  padding: 12px 14px;
  box-sizing: border-box;
  font: 12px/1.45 Tahoma, "MS Sans Serif", sans-serif;
  color: #e8dcc0;
  text-align: center;
}
.friends-ask-text { margin-bottom: 8px; }
.friends-ask-buttons { display: flex; gap: 8px; justify-content: center; }
.friends-ask-button {
  min-width: 61px;
  height: 21px;
  padding: 0 10px;
  border: 1px solid #8d7044;
  background: #2a2418;
  color: #ead7ae;
  font: 12px/21px Tahoma, "MS Sans Serif", sans-serif;
  cursor: pointer;
}
.friends-ask-button:hover:not(:disabled) { background: #3a3220; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE, installChrome);

export class FriendsPanel {
  private readonly root: HTMLElement;
  private readonly groupHeader: HTMLElement;
  private readonly groupToggle: HTMLElement;
  private readonly list: HTMLElement;
  private readonly empty: HTMLElement;
  private readonly guildGroupHeader: HTMLElement;
  private readonly guildGroupToggle: HTMLElement;
  private readonly guildList: HTMLElement;
  private readonly guildEmpty: HTMLElement;
  /** The Family group of `uimessenger.py:290-307,591-597`: at most one member,
   *  the spouse, drawn only while there is one. */
  private readonly loverHeader: HTMLElement;
  private readonly loverRow: HTMLElement;
  private readonly loverDot: HTMLElement;
  private readonly loverName: HTMLElement;
  private readonly addRow: HTMLElement;
  private readonly whisper: HTMLButtonElement;
  private readonly remove: HTMLButtonElement;
  private readonly nameField: HTMLInputElement;
  private readonly whisperBox: HTMLInputElement;
  private readonly inviteBox: HTMLInputElement;
  private readonly exchangeBox: HTMLInputElement;
  private readonly partyBox: HTMLInputElement;
  private readonly guildBox: HTMLInputElement;
  private readonly asks: HTMLElement;
  private drag: { readonly ox: number; readonly oy: number; readonly sl: number; readonly st: number } | null = null;

  /** Tokens already answered, so a second click on one prompt is not a second
   *  answer. See `ui/marriage.ts` for the same field and the same argument. */
  private readonly answered = new Set<number>();

  private drawn: FriendList | null = null;
  /** The last `Guild` frame, kept only so a header re-open can redraw the
   *  same roster it last had, the way `this.drawn` serves the Friends group. */
  private drawnGuild: Guild | null = null;
  /** The selection is a visible UI affordance, never a local copy of a friend
   *  or guildmate. Tracked with which group it came from, because Whisper and
   *  Delete-friend behave differently by group (see `pressRemove`). */
  private selectedName: string | null = null;
  private selectedGroup: "friends" | "guild" | null = null;
  private selectedOnline = false;
  /** Whether the Friends group is expanded. Groups in the original open by
   *  default (`uimessenger.py:472-473`, `member.Open()` from `__AddGroup`)
   *  and stay however a player left them across data refreshes -- closing one
   *  is a UI choice, not something a new `FriendList` frame should undo. */
  private friendsOpen = true;
  /** Same as `friendsOpen`, for the Guild group `__AddGroup` opens beside it
   *  (`uimessenger.py:477-479`). */
  private guildOpen = true;

  constructor(
    container: HTMLElement,
    private readonly actions: FriendActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = doc.createElement("div");
    this.root.id = "friends";
    this.root.className = "friends m2-board";
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-label", MESSENGER_TITLE);

    const title = elem(doc, "div", "m2-titlebar");
    title.id = "friends-title";
    // MESSENGER_TITLE (`locale_interface.txt:198`).
    title.textContent = MESSENGER_TITLE;
    this.root.setAttribute("aria-labelledby", title.id);
    this.wireDrag(title);

    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close";
    close.title = "Close";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => {
      this.setVisible(false);
      this.actions.onClose?.();
    });

    const body = elem(doc, "div", "friends-body");

    this.groupHeader = elem(doc, "div", "friends-group-header");
    this.groupHeader.setAttribute("role", "button");
    this.groupHeader.tabIndex = 0;
    this.groupToggle = elem(doc, "span", "friends-group-toggle");
    const groupLabel = elem(doc, "span", "friends-group-label", "Friends");
    this.groupHeader.append(this.groupToggle, groupLabel);
    const toggleGroup = (): void => {
      this.friendsOpen = !this.friendsOpen;
      this.paintGroup();
      if (this.drawn !== null) this.drawList(this.drawn.friends);
    };
    this.groupHeader.addEventListener("click", toggleGroup);
    this.groupHeader.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      toggleGroup();
    });

    this.list = elem(doc, "div", "friends-list");
    this.empty = elem(doc, "div", "friends-empty");
    // MESSENGER_EMPTY_LIST (`locale_game.txt:286` -- "Empty"), what
    // `uimessenger.py:616` names an empty friend group with.
    this.empty.textContent = "Empty";

    // The Guild group header. `__AddGroup` (`uimessenger.py:471-480`) builds
    // this beside the Friends group, not instead of it -- see the module
    // docstring for why an earlier pass here thought it could not be built.
    this.guildGroupHeader = elem(doc, "div", "friends-group-header friends-guild-header");
    this.guildGroupHeader.setAttribute("role", "button");
    this.guildGroupHeader.tabIndex = 0;
    this.guildGroupToggle = elem(doc, "span", "friends-group-toggle");
    {
      // A second, separately scoped `groupLabel` rather than a shared helper
      // parameterised on the text: kept a plain literal assignment so a
      // structural check reading this source can count both group labels the
      // same simple way it counts this one.
      const groupLabel = elem(doc, "span", "friends-group-label", "Guild");
      this.guildGroupHeader.append(this.guildGroupToggle, groupLabel);
    }
    const toggleGuildGroup = (): void => {
      this.guildOpen = !this.guildOpen;
      this.paintGuildGroup();
      if (this.drawnGuild !== null) this.drawGuildList(this.guildMembers(this.drawnGuild));
    };
    this.guildGroupHeader.addEventListener("click", toggleGuildGroup);
    this.guildGroupHeader.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      toggleGuildGroup();
    });

    this.guildList = elem(doc, "div", "friends-list friends-guild-list");
    this.guildEmpty = elem(doc, "div", "friends-empty friends-guild-empty");
    // MESSENGER_EMPTY_LIST again -- an empty Guild group (no guild, or one
    // with nobody else in it) is drawn the same way an empty Friends group
    // is (`uimessenger.py:614-617` names both groups with the same filler).
    this.guildEmpty.textContent = "Empty";

    this.loverHeader = elem(doc, "div", "friends-group-header friends-family-header");
    const familyLabel = elem(doc, "span", "friends-group-label");
    familyLabel.textContent = "Family";
    this.loverHeader.append(elem(doc, "span", "friends-group-toggle", "-"), familyLabel);
    this.loverHeader.hidden = true;
    this.loverRow = elem(doc, "div", "friends-family-row");
    this.loverDot = elem(doc, "span", "friends-dot");
    this.loverName = elem(doc, "span", "friends-name");
    this.loverRow.append(this.loverDot, this.loverName);
    this.loverRow.hidden = true;

    body.append(
      this.groupHeader,
      this.list,
      this.empty,
      this.guildGroupHeader,
      this.guildList,
      this.guildEmpty,
      this.loverHeader,
      this.loverRow,
    );

    // `OnPressAddFriendButton` opens an `InputDialog` titled
    // `MESSENGER_ADD_FRIEND` (`uimessenger.py:501-503`). The Add icon toggles
    // this compact field row; submit keeps the same wire as before.
    this.addRow = elem(doc, "div", "friends-add");
    this.addRow.hidden = true;
    this.nameField = elem(doc, "input", "friends-add-name");
    this.nameField.type = "text";
    this.nameField.placeholder = "name";
    this.nameField.setAttribute("aria-label", "Add friend");
    const ask = doc.createElement("button");
    ask.type = "button";
    ask.className = "friends-ask-submit";
    // MESSENGER_ADD_FRIEND (`locale_interface.txt:194` -- "Add friend").
    ask.textContent = "Add friend";
    ask.addEventListener("click", () => this.send());
    this.nameField.addEventListener("keydown", (event) => {
      if (yieldToCandidateWindow(event as KeyboardEvent)) return;
      if ((event as KeyboardEvent).key === "Enter") this.send();
    });
    this.addRow.append(this.nameField, ask);

    const switches = elem(doc, "div", "friends-switches");
    this.whisperBox = this.switch(doc, switches, "Refuse whispers");
    this.inviteBox = this.switch(doc, switches, "Refuse friend requests");
    // The other three of the original's five options-window switches
    // (`uioption.py:69-73,105-114,176-195`), each `locale_interface.txt`
    // label ("Trade"/"Group"/"Guild") phrased the way the two above already
    // are.
    this.exchangeBox = this.switch(doc, switches, "Refuse trades");
    this.partyBox = this.switch(doc, switches, "Refuse party invites");
    this.guildBox = this.switch(doc, switches, "Refuse guild invites");

    // Bottom icon row: Add / Whisper / Remove / Guild
    // (`messengerwindow.py` children, BUTTON_X_STEP = 40).
    const icons = elem(doc, "div", "friends-icons");

    const addIcon = doc.createElement("button");
    addIcon.type = "button";
    addIcon.className = "friends-icon friends-add-button";
    // MESSENGER_ADD_FRIEND tooltip.
    addIcon.title = "Add friend";
    addIcon.setAttribute("aria-label", "Add friend");
    addIcon.textContent = "Add friend";
    addIcon.addEventListener("click", () => {
      this.addRow.hidden = !this.addRow.hidden;
      if (!this.addRow.hidden) this.nameField.focus();
    });

    // The native messenger has one selected member and enables its separate
    // Whisper / Remove buttons from that member's capabilities
    // (`uimessenger.py:559-578`). The list remains server-owned; selection is
    // only the current interaction target.
    this.whisper = doc.createElement("button");
    this.whisper.type = "button";
    this.whisper.className = "friends-icon friends-whisper";
    // MESSENGER_WHISPER (`locale_interface.txt`).
    this.whisper.title = "Whisper";
    this.whisper.setAttribute("aria-label", "Whisper");
    this.whisper.textContent = "Whisper";
    this.whisper.disabled = true;
    this.whisper.addEventListener("click", () => this.pressWhisper());

    this.remove = doc.createElement("button");
    this.remove.type = "button";
    this.remove.className = "friends-icon friends-remove";
    // MESSENGER_DELETE_FRIEND (`locale_interface.txt:195` -- "Delete friend").
    this.remove.title = "Delete friend";
    this.remove.setAttribute("aria-label", "Delete friend");
    this.remove.textContent = "Delete friend";
    this.remove.disabled = true;
    this.remove.addEventListener("click", () => this.pressRemove());

    const guildButton = doc.createElement("button");
    guildButton.type = "button";
    guildButton.className = "friends-icon friends-guild-button";
    // MESSENGER_OPEN_GUILD (`locale_interface.txt:197` -- "Open Guild Window").
    guildButton.title = "Open Guild Window";
    guildButton.setAttribute("aria-label", "Open Guild Window");
    guildButton.textContent = "Open Guild Window";
    guildButton.addEventListener("click", () => this.actions.onOpenGuild?.());

    icons.append(addIcon, this.whisper, this.remove, guildButton);

    this.root.append(title, close, body, this.addRow, switches, icons);

    this.paintGroup();
    this.paintGuildGroup();

    this.asks = elem(doc, "div", "friends-ask");
    this.asks.hidden = true;

    container.append(this.root, this.asks);
    // `MessengerWindow.OnPressEscapeKey` closes the window and consumes the
    // key (`uimessenger.py:554-556`). Listen at the document so Escape works
    // even when focus is in a name field, and close the shared social surface
    // through the host callback rather than leaving its opener expanded.
    doc.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      this.setVisible(false);
      this.actions.onClose?.();
    });
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get asking(): boolean {
    return !this.asks.hidden;
  }

  /** Whether this window owns the keyboard. Global game shortcuts must not
   *  fire while a player is typing a name. */
  get typing(): boolean {
    return this.root.ownerDocument.activeElement === this.nameField;
  }

  /** The names drawn in the Friends group, in order. For tests. */
  get shownNames(): string[] {
    if (this.drawn === null || !this.friendsOpen) return [];
    return this.orderByOnline(this.drawn.friends).map((row) => row.name);
  }

  /** The names drawn in the Guild group, in order. For tests. */
  get shownGuildNames(): string[] {
    if (this.drawnGuild === null || !this.guildOpen) return [];
    return this.orderByOnline(this.guildMembers(this.drawnGuild)).map((row) => row.name);
  }

  /** The tokens of the prompts on screen. For tests, for `MarriagePanel`'s
   *  reason. */
  get shownTokens(): number[] {
    return this.drawn === null ? [] : this.drawn.asks.map((row) => row.token);
  }

  /** Show or hide the whole panel. The list itself is never emptied by this --
   *  it is the server's, and a hidden panel is a keypress away from being
   *  visible again with the same rows. */
  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
  }

  setLover(status: {
    state: number;
    partner_name: string;
    partner_online: boolean;
    point: number;
  }): void {
    const has = status.state !== 0 && status.partner_name !== "";
    this.loverHeader.hidden = !has;
    this.loverRow.hidden = !has;
    if (!has) {
      this.loverName.textContent = "";
      this.loverRow.removeAttribute("title");
      return;
    }
    this.loverName.textContent = status.partner_name;
    // `MessengerItem.Online`/`Offline` (`uimessenger.py:56-70`) is the whole of
    // what the dot says; the row is never taken away for a spouse who is
    // merely signed out.
    this.loverRow.classList.toggle("friends-family-row-online", status.partner_online);
    // AFF_LOVE_POINT, the tooltip `uimessenger.py:92-98` builds over the row.
    this.loverRow.title = `${status.partner_name}\nLove: ${status.point}`;
  }

  /** Draw one `FriendList`, whole. See the frame: there is no delta. */
  update(list: FriendList): void {
    this.drawn = list;
    // Refreshing data is not an opener. The original wires the messenger to
    // the taskbar and toggles it only from that action
    // (`interfaceModule.py:124-136,901-906`). A login frame that made it
    // visible would turn the existence of the feature into permanent HUD.
    this.drawList(list.friends);
    // The switches come from the frame and are never remembered. See the
    // module docstring: `bBlockMode` clears on every login.
    this.whisperBox.checked = list.blocks_whisper;
    this.inviteBox.checked = list.blocks_invite;
    this.exchangeBox.checked = list.blocks_exchange;
    this.partyBox.checked = list.blocks_party_invite;
    this.guildBox.checked = list.blocks_guild_invite;
    this.drawAsks(list.asks);
  }

  updateGuild(guild: Guild): void {
    this.drawnGuild = guild;
    this.drawGuildList(this.guildMembers(guild));
  }

  /** `Guild.members` minus this reader's own row (`your_slot` names exactly
   *  which one, `net/messages.ts:4088-4209`) -- see the module docstring on
   *  why this build excludes it here. */
  private guildMembers(guild: Guild): GuildMember[] {
    return guild.members.filter((_member, slot) => slot !== guild.your_slot);
  }

  private orderByOnline<T extends { online: boolean }>(rows: readonly T[]): T[] {
    return [...rows.filter((row) => row.online), ...rows.filter((row) => !row.online)];
  }

  /** One row, shared by both groups: a name, a presence dot, click-to-select
   *  and double-click-to-whisper (`uimessenger.py:580-586`). `group` tags the
   *  row so selection and `pressRemove` can tell a Friends row from a Guild
   *  one apart (`dataset.name` alone is not unique across both groups). */
  private buildRow(
    doc: Document,
    member: { name: string; online: boolean },
    group: "friends" | "guild",
  ): HTMLElement {
    const row = elem(
      doc,
      "div",
      `friends-row ${member.online ? "friends-row-online" : "friends-row-offline"}`,
    );
    row.dataset.name = member.name;
    row.dataset.group = group;
    row.setAttribute("role", "button");
    row.tabIndex = 0;
    const selected = member.name === this.selectedName && group === this.selectedGroup;
    row.setAttribute("aria-selected", String(selected));
    row.setAttribute("aria-pressed", String(selected));
    if (selected) row.classList.add("friends-row-selected");
    row.addEventListener("click", () => this.select(member, group));
    row.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      this.select(member, group);
    });
    const dot = elem(doc, "span", "friends-dot");
    const name = elem(doc, "span", "friends-name", member.name);
    // `OnDoubleClickItem` uses the selected member and whispers only when it
    // is online (`uimessenger.py:580-586`); selecting first matters when a
    // test or assistive input sends a double-click without its two clicks.
    row.addEventListener("dblclick", () => {
      this.select(member, group);
      this.pressWhisper();
    });
    row.append(dot, name);
    return row;
  }

  private drawList(friends: readonly FriendRow[]): void {
    const doc = this.list.ownerDocument;
    this.list.replaceChildren();
    if (
      this.selectedGroup === "friends" &&
      !friends.some((friend) => friend.name === this.selectedName)
    ) {
      this.selectedName = null;
      this.selectedGroup = null;
      this.selectedOnline = false;
    }

    if (!this.friendsOpen) {
      // `Close()` hides every member row (`uimessenger.py:214-218`), and
      // `OnRefreshList` draws neither members nor the "Empty" filler for a
      // closed group (`:591-619`) -- collapsing the header hides both.
      this.empty.hidden = true;
      this.paintSelection();
      return;
    }

    const ordered = this.orderByOnline(friends);
    for (const friend of ordered) this.list.append(this.buildRow(doc, friend, "friends"));
    this.empty.hidden = ordered.length > 0;
    this.paintSelection();
  }

  /** `drawList`'s Guild-group twin: same open/closed, same online-first
   *  ordering, same "Empty" filler -- the whole difference is which frame
   *  and which container. */
  private drawGuildList(members: readonly GuildMember[]): void {
    const doc = this.guildList.ownerDocument;
    this.guildList.replaceChildren();
    if (
      this.selectedGroup === "guild" &&
      !members.some((member) => member.name === this.selectedName)
    ) {
      this.selectedName = null;
      this.selectedGroup = null;
      this.selectedOnline = false;
    }

    if (!this.guildOpen) {
      this.guildEmpty.hidden = true;
      this.paintSelection();
      return;
    }

    const ordered = this.orderByOnline(members);
    for (const member of ordered) this.guildList.append(this.buildRow(doc, member, "guild"));
    this.guildEmpty.hidden = ordered.length > 0;
    this.paintSelection();
  }

  private paintGroup(): void {
    this.groupHeader.setAttribute("aria-expanded", String(this.friendsOpen));
    this.groupToggle.textContent = this.friendsOpen ? "▾" : "▸";
  }

  /** `paintGroup`'s Guild-group twin. */
  private paintGuildGroup(): void {
    this.guildGroupHeader.setAttribute("aria-expanded", String(this.guildOpen));
    this.guildGroupToggle.textContent = this.guildOpen ? "▾" : "▸";
  }

  private select(member: { name: string; online: boolean }, group: "friends" | "guild"): void {
    this.selectedName = member.name;
    this.selectedGroup = group;
    this.selectedOnline = member.online;
    this.paintSelection();
  }

  private paintSelection(): void {
    // Scoped to `this.root` rather than to one list: the native window keeps
    // one `selectedItem` across every group (`uimessenger.py:559-578`), and
    // the Whisper/Delete-friend buttons below both lists act on whichever
    // group drew the selected row.
    for (const row of this.root.querySelectorAll<HTMLElement>(".friends-row")) {
      const selected =
        row.dataset.name === this.selectedName && row.dataset.group === this.selectedGroup;
      row.classList.toggle("friends-row-selected", selected);
      row.setAttribute("aria-selected", String(selected));
      row.setAttribute("aria-pressed", String(selected));
    }
    this.whisper.disabled = this.selectedName === null || !this.selectedOnline;
    // `MessengerFriendItem.CanRemove` always returns true; a guild row's own
    // `CanRemove` needs `AUTH_REMOVE_MEMBER` and a live enemy-guild check
    // (`uimessenger.py:235-236,256-265`) this panel does not carry -- see the
    // module docstring on why guild expulsion stays in `ui/guild.ts` alone.
    this.remove.disabled = this.selectedName === null || this.selectedGroup !== "friends";
  }

  private pressWhisper(): void {
    if (this.selectedName === null || !this.selectedOnline) return;
    this.actions.onWhisper?.(this.selectedName);
  }

  private pressRemove(): void {
    if (this.selectedName === null || this.selectedGroup !== "friends") return;
    this.actions.onRemove?.(this.selectedName);
  }

  private drawAsks(asks: readonly FriendAskRow[]): void {
    const doc = this.asks.ownerDocument;
    this.asks.replaceChildren();
    const live = asks.filter((row) => !this.answered.has(row.token));
    for (const row of live) {
      const card = elem(doc, "div", "friends-ask-card m2-thinboard");
      const text = elem(doc, "div", "friends-ask-text");
      // MESSENGER_DO_YOU_ACCEPT_ADD_FRIEND_1 (`locale_game.txt:281` -- "%s added
      // you as a friend."); `_2`'s "Accept?" is left to the Yes/No card below.
      text.textContent = `${row.name || "Somebody"} added you as a friend.`;
      const buttons = elem(doc, "div", "friends-ask-buttons");
      const yes = doc.createElement("button");
      yes.type = "button";
      yes.className = "friends-ask-button";
      yes.textContent = "Yes";
      yes.addEventListener("click", () => this.answer(row.token, true));
      const no = doc.createElement("button");
      no.type = "button";
      no.className = "friends-ask-button";
      no.textContent = "No";
      no.addEventListener("click", () => this.answer(row.token, false));
      buttons.append(yes, no);
      card.append(text, buttons);
      this.asks.append(card);
    }
    this.asks.hidden = live.length === 0;
  }

  private answer(token: number, accept: boolean): void {
    if (this.answered.has(token)) return;
    this.answered.add(token);
    this.actions.onAnswer?.(token, accept);
    if (this.drawn !== null) this.drawAsks(this.drawn.asks);
  }

  private send(): void {
    const name = this.nameField.value.trim();
    if (name === "") return;
    // The field is cleared and nothing else happens. No row is drawn, no
    // "pending" state is kept and no name is remembered as having failed --
    // see the module docstring on why a client-side memory of refusals would
    // rebuild the oracle.
    this.nameField.value = "";
    this.addRow.hidden = true;
    this.actions.onAdd?.(name);
  }

  private wireDrag(handle: HTMLElement): void {
    handle.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;
      const rect = this.root.getBoundingClientRect();
      this.drag = {
        ox: event.clientX,
        oy: event.clientY,
        sl: rect.left,
        st: rect.top,
      };
      handle.setPointerCapture(event.pointerId);
    });
    handle.addEventListener("pointermove", (event) => {
      if (this.drag === null) return;
      const left = this.drag.sl + (event.clientX - this.drag.ox);
      const top = this.drag.st + (event.clientY - this.drag.oy);
      this.root.style.left = `${Math.round(left)}px`;
      this.root.style.top = `${Math.round(top)}px`;
      this.root.style.right = "auto";
    });
    const endDrag = (): void => {
      this.drag = null;
    };
    handle.addEventListener("pointerup", endDrag);
    handle.addEventListener("pointercancel", endDrag);
  }

  private switch(
    doc: Document,
    into: HTMLElement,
    label: string,
  ): HTMLInputElement {
    const wrap = elem(doc, "label", "friends-switch");
    const box = doc.createElement("input");
    box.type = "checkbox";
    box.addEventListener("change", () =>
      // All five flags together, always, because `SetBlockMode` overwrites
      // the whole byte (`char.cpp:5896-5898`) -- sending one would clear
      // the rest.
      this.actions.onBlock?.(
        this.whisperBox.checked,
        this.inviteBox.checked,
        this.exchangeBox.checked,
        this.partyBox.checked,
        this.guildBox.checked,
      ),
    );
    const text = doc.createElement("span");
    text.textContent = label;
    wrap.append(box, text);
    into.append(wrap);
    return box;
  }
}
